import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiFilter, FiX } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import PriceRangeSlider from '../components/PriceRangeSlider';
import SearchBar from '../components/SearchBar';
import { useProducts } from '../hooks/useProducts';
import { useBrands } from '../hooks/useBrands';
import { useCategories } from '../hooks/useCategories';
import { useSubcategories } from '../hooks/useSubcategories';
import { useLanguage } from '../context/LanguageContext';
import { slugify } from '../utils/slugify';
import { sortProductsByBoostAndPopularity, fetchSalesDataPublicSafe, fetchReviewsData } from '../utils/productSorting';

// Lazy load CategoryCard
const CategoryCard = lazy(() => import('../components/CategoryCard'));

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const HeaderActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  color: #065f46;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  white-space: nowrap;
`;

const CategoriesHeaderRow = styled(TitleRow)`
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.9);
  
  @media (max-width: 968px) {
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    gap: 12px;
  }
`;

const MobileSearchWrapper = styled.div`
  display: none;
  
  @media (max-width: 968px) {
    display: block;
    width: 100%;
    margin-bottom: 12px;
  }
`;

const MobileFilterToggle = styled.button`
  display: none;
  
  @media (max-width: 968px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 18px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border: 2px solid rgba(226, 232, 240, 0.8);
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 12px;
    
    &:hover {
      border-color: #5efba9;
      box-shadow: 0 4px 12px rgba(94, 251, 169, 0.2);
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    svg {
      font-size: 18px;
      transition: transform 0.3s;
    }
  }
`;

const MobileFilterPanel = styled.div`
  display: none;
  
  @media (max-width: 968px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 12px;
    border: 2px solid rgba(226, 232, 240, 0.8);
    box-shadow: 
      0 4px 6px -1px rgba(0, 0, 0, 0.05),
      0 2px 4px -1px rgba(0, 0, 0, 0.03);
    margin-bottom: 16px;
    animation: slideDown 0.3s ease-out;
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

const MobileFilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const MobileFilterLabel = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
`;

const MobileBrandsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const MobilePriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
`;

const MobilePriceDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
  border-radius: 10px;
  color: #0f172a;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
`;

const MobileClearFilters = styled.button`
  width: 100%;
  padding: 12px 18px;
  margin-top: 8px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
    color: #374151;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DesktopToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 968px) {
    display: none;
  }
`;

const ToolbarTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  
  @media (max-width: 968px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 968px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ToolbarBottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  width: 100%;
  
  @media (max-width: 968px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding-top: 12px;
  }
`;

const SortSelect = styled.select`
  padding: 11px 18px;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  min-width: 180px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: #5efba9;
    box-shadow: 0 2px 8px rgba(94, 251, 169, 0.15);
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.15), 0 2px 8px rgba(94, 251, 169, 0.2);
  }
  
  @media (max-width: 968px) {
    width: 100%;
    min-width: unset;
    padding: 14px 18px;
    font-size: 15px;
    min-height: 48px;
    touch-action: manipulation;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const BrandsFilterContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  flex: 1;
  min-width: 200px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
  
  @media (max-width: 968px) {
    width: 100%;
  }
`;

const BrandChip = styled.button`
  padding: 7px 16px;
  border: 2px solid ${props => props.$selected ? 'rgba(94, 251, 169, 0.6)' : 'rgba(226, 232, 240, 0.8)'};
  border-radius: 20px;
  background: ${props => props.$selected 
    ? 'linear-gradient(135deg, #5efba9 0%, #34d399 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  color: ${props => props.$selected ? '#0f172a' : '#475569'};
  font-size: 13px;
  font-weight: ${props => props.$selected ? '700' : '600'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: ${props => props.$selected 
    ? '0 2px 8px rgba(94, 251, 169, 0.3)' 
    : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    border-color: ${props => props.$selected ? 'rgba(94, 251, 169, 0.8)' : '#5efba9'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$selected 
      ? '0 4px 12px rgba(94, 251, 169, 0.4)' 
      : '0 3px 8px rgba(94, 251, 169, 0.2)'};
    background: ${props => props.$selected
      ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 968px) {
    padding: 10px 18px;
    font-size: 14px;
    min-height: 44px;
    touch-action: manipulation;
  }
`;

const PriceFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 968px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const PriceSliderWrapper = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 400px;
  
  @media (max-width: 968px) {
    width: 100%;
    max-width: 100%;
  }
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 36px;
  background: linear-gradient(180deg, transparent 0%, rgba(226, 232, 240, 0.8) 20%, rgba(226, 232, 240, 0.8) 80%, transparent 100%);
  
  @media (max-width: 968px) {
    display: none;
  }
`;

const PriceLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::after {
    content: ':';
    color: #9ca3af;
  }
`;

const PriceDisplay = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  min-width: 100px;
  text-align: center;
  padding: 8px 14px;
  background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
  letter-spacing: 0.3px;
  
  @media (max-width: 968px) {
    width: 100%;
    min-width: unset;
  }
`;

const SubcategoriesSection = styled.div`
  margin-bottom: 32px;
  border-bottom: 2px solid #e5e7eb;
`;

const SubcategoriesTabs = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 16px;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const SubcategoryTab = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' : 'white'};
  color: ${props => props.active ? '#065f46' : '#475569'};
  font-size: 14px;
  font-weight: ${props => props.active ? '700' : '600'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${props => props.active ? '#5efba9' : 'transparent'};
  box-shadow: ${props => props.active ? '0 2px 8px rgba(94, 251, 169, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)'};
  position: relative;

  &:hover {
    background: ${props => props.active
    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
    : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.active
    ? '0 4px 12px rgba(94, 251, 169, 0.3)'
    : '0 4px 12px rgba(0, 0, 0, 0.1)'};
    border-color: ${props => props.active ? '#5efba9' : '#cbd5e1'};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -18px;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.active ? '80%' : '0'};
    height: 3px;
    background: linear-gradient(90deg, #5efba9 0%, #10b981 100%);
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

const ProductsSection = styled.div`
  flex: 1;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  
  @media (max-width: 768px) {
    margin-top: 32px;
    padding-top: 24px;
    flex-wrap: wrap;
  }
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: 2px solid ${props => props.$active ? 'rgba(94, 251, 169, 0.6)' : 'rgba(226, 232, 240, 0.8)'};
  border-radius: 10px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #5efba9 0%, #34d399 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  color: ${props => props.$active ? '#0f172a' : '#475569'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '700' : '600'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 40px;
  box-shadow: ${props => props.$active 
    ? '0 2px 8px rgba(94, 251, 169, 0.3)' 
    : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  
  &:hover:not(:disabled) {
    border-color: ${props => props.$active ? 'rgba(94, 251, 169, 0.8)' : '#5efba9'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$active 
      ? '0 4px 12px rgba(94, 251, 169, 0.4)' 
      : '0 3px 8px rgba(94, 251, 169, 0.2)'};
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #f1f5f9;
    border-color: #e2e8f0;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 13px;
    min-width: 36px;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  padding: 0 12px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 0 8px;
    width: 100%;
    text-align: center;
    margin-top: 8px;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

function Categories() {
  const { categorySlug, subcategorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { products: allProducts, loading: productsLoading } = useProducts();
  const { brands: allBrands } = useBrands();
  const { categories: allCategories, loading: categoriesLoading } = useCategories();
  const { isArabic, language } = useLanguage();
  
  // Check if we're specifically viewing the categories list page
  // Changed: /categories now shows all products, use /categories/browse for category list
  const isCategoriesPage = location.pathname === '/categories/browse' && !categorySlug;

  // Prepare categories for display (only if showing categories page)
  const displayCategories = useMemo(() => {
    if (!isCategoriesPage) return [];
    const icons = ['🍔', '💄', '👗', '📱', '🌸', '🎮', '👶'];
    return allCategories.slice(0, 12).map((cat, index) => ({
      id: cat.id,
      name: isArabic ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
      icon: icons[index % icons.length],
      image: cat.img || null
    }));
  }, [allCategories, isArabic, isCategoriesPage]);

  const [sortBy, setSortBy] = useState('popular');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDiscountedOnly, setShowDiscountedOnly] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [salesData, setSalesData] = useState({});
  const [reviewsData, setReviewsData] = useState({});
  const [popularityLoading, setPopularityLoading] = useState(true);
  const [popularityReady, setPopularityReady] = useState(false);
  const productsPerPage = 12;

  // Determine page title and filter products
  let pageTitle = isArabic ? 'جميع المنتجات' : 'All Products';
  let filteredProducts = allProducts.filter(p => p.status === 'active');
  let category = null;

  if (categorySlug) {
    // Find category by slug (from name_en or name)
    category = allCategories.find(c => {
      const nameSlug = slugify(c.name_en || c.name);
      return nameSlug === categorySlug;
    });

    if (category) {
      pageTitle = isArabic ? (category.name_ar || category.name_en || category.name) : (category.name_en || category.name);
      filteredProducts = allProducts.filter(p => {
        return p.category_id === category.id && p.status === 'active';
      });
    } else {
      pageTitle = isArabic ? 'الفئة غير موجودة' : 'Category Not Found';
    }
  }

  // Fetch subcategories for the current category
  const { subcategories, loading: subcategoriesLoading } = useSubcategories(category?.id || null);

  // Determine selected subcategory from URL or state
  useEffect(() => {
    if (subcategorySlug && subcategories.length > 0) {
      const subcat = subcategories.find(s => {
        const subcatSlug = s.slug || slugify(s.name_en || s.name_ar || '');
        return subcatSlug === subcategorySlug;
      });
      if (subcat) {
        setSelectedSubcategory(subcat.id);
      }
    } else {
      setSelectedSubcategory(null);
    }
  }, [subcategorySlug, subcategories]);

  // Apply subcategory filter
  if (selectedSubcategory) {
    filteredProducts = filteredProducts.filter(p => p.subcategory_id === selectedSubcategory);
  }

  // Apply brand filter
  if (selectedBrands.length > 0) {
    filteredProducts = filteredProducts.filter(p => selectedBrands.includes(p.brand_id));
  }

  // Apply price range filter
  filteredProducts = filteredProducts.filter(p => {
    const price = parseFloat(p.price) || 0;
    return price >= priceRange.min && price <= priceRange.max;
  });

  // Apply discount filter
  if (showDiscountedOnly) {
    filteredProducts = filteredProducts.filter(p => {
      const discount = p.dis && p.dis !== 'no' ? parseFloat(p.dis) : null;
      return discount !== null && discount > 0;
    });
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filteredProducts = filteredProducts.filter(p => {
      const productName = (p.name || '').toLowerCase();
      const productNameAr = (p.name_ar || '').toLowerCase();
      const productBrand = allBrands.find(b => b.id === p.brand_id);
      const brandName = (productBrand?.name || '').toLowerCase();

      return productName.includes(query) ||
        productNameAr.includes(query) ||
        brandName.includes(query);
    });
  }

  // Fetch sales and reviews data for products
  useEffect(() => {
    let cancelled = false;

    const loadProductData = async () => {
      // Wait until products query finishes before deciding readiness.
      if (productsLoading) return;

      if (allProducts.length === 0) {
        if (!cancelled) {
          setPopularityLoading(false);
          setPopularityReady(true);
        }
        return;
      }
      
      const activeProductIds = allProducts
        .filter(p => p.status === 'active')
        .map(p => p.id);
      
      if (activeProductIds.length === 0) {
        if (!cancelled) {
          setPopularityLoading(false);
          setPopularityReady(true);
        }
        return;
      }

      try {
        // Show blocking loader only for first popularity load.
        if (!popularityReady && !cancelled) {
          setPopularityLoading(true);
        }
        const [sales, reviews] = await Promise.all([
          fetchSalesDataPublicSafe(activeProductIds),
          fetchReviewsData(activeProductIds)
        ]);

        if (!cancelled) {
          setSalesData(sales);
          setReviewsData(reviews);
          setPopularityReady(true);
        }
      } catch (error) {
        console.error('Error loading product data:', error);
        if (!cancelled) {
          setPopularityReady(true);
        }
      } finally {
        if (!cancelled) {
          setPopularityLoading(false);
        }
      }
    };

    loadProductData();
    return () => {
      cancelled = true;
    };
  }, [allProducts, productsLoading]);

  // Check if any filters are active (for boost logic)
  const hasActiveFilters = useMemo(() => {
    return (
      selectedBrands.length > 0 ||
      priceRange.min > 0 ||
      priceRange.max < 1000 ||
      searchQuery.trim() !== '' ||
      sortBy !== 'popular' ||
      showDiscountedOnly
    );
  }, [selectedBrands, priceRange, searchQuery, sortBy, showDiscountedOnly]);

  // Sort products - boosted products appear at top, then by sales and reviews
  const sortedProducts = useMemo(() => {
    const products = [...filteredProducts];
    
    // Apply sorting based on sortBy
    let sorted;
    switch (sortBy) {
      case 'price-low':
        sorted = products.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-high':
        sorted = products.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'newest':
        sorted = products.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'popular':
      default:
        // For popular/default view, sort by boost and popularity
        sorted = sortProductsByBoostAndPopularity(products, salesData, reviewsData);
        break;
    }
    
    // If other sort options are selected, still prioritize boosted products
    if (sortBy !== 'popular') {
      sorted = [...sorted].sort((a, b) => {
        // Boosted products come first
        if (a.boost && !b.boost) return -1;
        if (!a.boost && b.boost) return 1;
        return 0;
      });
    }
    
    return sorted;
  }, [filteredProducts, sortBy, salesData, reviewsData]);

  // Format products for ProductCard
  const allDisplayProducts = sortedProducts.map(product => {
    const productBrand = allBrands.find(brand => brand.id === product.brand_id);
    const discount = product.dis && product.dis !== 'no' ? parseFloat(product.dis) : null;
    const discountedPrice = discount && product.price ? (product.price * (1 - discount / 100)).toFixed(2) : null;

    return {
      id: product.id,
      title: product.name,
      name: product.name,
      name_ar: product.name_ar,
      price: discount ? discountedPrice : product.price,
      originalPrice: discount ? product.price : null,
      discount,
      currency: product.currency,
      image: product.img || product.images?.[0] || 'https://via.placeholder.com/300x240',
      brand: productBrand?.name,
      seller: { id: product.brand_id || 1, name: productBrand?.name || 'Seller' }
    };
  });

  // Pagination calculations
  const totalPages = Math.ceil(allDisplayProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const displayProducts = allDisplayProducts.slice(startIndex, endIndex);

  // Filter brands based on current category
  const availableBrands = useMemo(() => {
    // First filter to only active brands
    const activeBrands = allBrands.filter(brand => brand.is_active === true || brand.is_active === 'true' || brand.is_active === 1);
    
    // If no category is selected, show all brands that have active products
    if (!category) {
      const brandIdsWithProducts = [...new Set(
        allProducts
          .filter(p => p.status === 'active')
          .map(p => p.brand_id)
          .filter(Boolean)
      )];
      
      return activeBrands.filter(brand => 
        brandIdsWithProducts.includes(brand.id)
      );
    }
    
    // If a category is selected, only show brands that have products in that category
    const categoryId = String(category.id);
    const brandIdsInCategory = [...new Set(
      allProducts
        .filter(p => {
          const productCategoryId = p.category_id ? String(p.category_id) : null;
          return productCategoryId === categoryId && p.status === 'active';
        })
        .map(p => p.brand_id)
        .filter(Boolean)
    )];
    
    return activeBrands.filter(brand => 
      brandIdsInCategory.includes(brand.id)
    );
  }, [allBrands, allProducts, category]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBrands, priceRange, searchQuery, selectedSubcategory, sortBy, categorySlug]);

  const handleBrandToggle = (brandId) => {
    setSelectedBrands(prev =>
      prev.includes(brandId)
        ? prev.filter(id => id !== brandId)
        : [...prev, brandId]
    );
  };

  const handleClearFilters = () => {
    setSelectedBrands([]);
    setPriceRange({ min: 0, max: 1000 });
    setSortBy('popular');
  };

  // If no categorySlug, show all products (not categories)
  // Filter all active products when no category is selected
  if (!categorySlug && !isCategoriesPage) {
    filteredProducts = allProducts.filter(p => p.status === 'active');
  }

  // If showing categories page, render categories grid
  if (isCategoriesPage) {
    return (
      <Container>
        <Header>
          <CategoriesHeaderRow>
            <Title>{isArabic ? 'جميع الفئات' : 'All Categories'}</Title>
            <HeaderActionLink to="/products">
              {isArabic ? 'عرض كل المنتجات' : 'View All Products'}
            </HeaderActionLink>
          </CategoriesHeaderRow>
        </Header>
        {categoriesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading categories...
          </div>
        ) : displayCategories.length > 0 ? (
          <Suspense fallback={<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>}>
            <CategoriesGrid>
              {displayCategories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </CategoriesGrid>
          </Suspense>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {isArabic ? 'لا توجد فئات متاحة' : 'No categories available'}
          </div>
        )}
      </Container>
    );
  }

  const showProductsLoading = productsLoading || (sortBy === 'popular' && !popularityReady && popularityLoading);

  if (showProductsLoading) {
    return (
      <Container>
        <Header>
          <Title>{isArabic ? 'جميع المنتجات' : 'All Products'}</Title>
        </Header>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          {isArabic ? 'جاري ترتيب المنتجات...' : 'Loading and sorting products...'}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TitleRow>
          <Title>{!categorySlug ? (isArabic ? 'جميع المنتجات' : 'All Products') : pageTitle}</Title>
        </TitleRow>
        
        {/* Mobile Search Bar - Always Visible */}
        <MobileSearchWrapper>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </MobileSearchWrapper>
        
        {/* Mobile Filter Toggle */}
        <MobileFilterToggle onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiFilter />
            {isArabic ? 'الفلاتر' : 'Filters'}
            {(selectedBrands.length > 0 || priceRange.min > 0 || priceRange.max < 1000 || showDiscountedOnly) && (
              <span style={{ 
                background: '#5efba9', 
                color: '#0f172a', 
                borderRadius: '12px', 
                padding: '2px 8px', 
                fontSize: '12px', 
                fontWeight: '700' 
              }}>
                {selectedBrands.length + (priceRange.min > 0 || priceRange.max < 1000 ? 1 : 0) + (showDiscountedOnly ? 1 : 0)}
              </span>
            )}
          </span>
          {isMobileFilterOpen ? <FiX /> : <FiChevronRight style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />}
        </MobileFilterToggle>

        {/* Mobile Filter Panel */}
        <MobileFilterPanel $isOpen={isMobileFilterOpen}>
          {/* Brands Filter */}
          {availableBrands.length > 0 && (
            <MobileFilterSection>
              <MobileFilterLabel>{isArabic ? 'العلامات التجارية' : 'Brands'}</MobileFilterLabel>
              <MobileBrandsContainer>
                {availableBrands.map(brand => (
                  <BrandChip
                    key={brand.id}
                    $selected={selectedBrands.includes(brand.id)}
                    onClick={() => handleBrandToggle(brand.id)}
                  >
                    {brand.name}
                  </BrandChip>
                ))}
              </MobileBrandsContainer>
            </MobileFilterSection>
          )}

          {/* Price Range Filter */}
          <MobileFilterSection>
            <MobileFilterLabel>{isArabic ? 'نطاق السعر' : 'Price Range'}</MobileFilterLabel>
            <MobilePriceContainer>
              <PriceRangeSlider
                min={0}
                max={1000}
                value={priceRange}
                onChange={setPriceRange}
                inline={true}
              />
              <MobilePriceDisplay>
                {priceRange.min} - {priceRange.max} OMR
              </MobilePriceDisplay>
            </MobilePriceContainer>
          </MobileFilterSection>

          {/* Discounted Items Filter */}
          <MobileFilterSection>
            <MobileFilterLabel>{isArabic ? 'العروض والخصومات' : 'Deals & Discounts'}</MobileFilterLabel>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={showDiscountedOnly}
                onChange={(e) => setShowDiscountedOnly(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#5efba9', cursor: 'pointer' }}
              />
              <span>{isArabic ? 'منتجات مخفضة' : 'Discounted Items'}</span>
            </label>
          </MobileFilterSection>

          {/* Sort */}
          <MobileFilterSection>
            <MobileFilterLabel>{isArabic ? 'ترتيب حسب' : 'Sort By'}</MobileFilterLabel>
            <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="popular">{isArabic ? 'الأكثر مبيعاً' : 'Best Seller'}</option>
              <option value="price-low">{isArabic ? 'السعر: من الأقل للأعلى' : 'Price: Low to High'}</option>
              <option value="price-high">{isArabic ? 'السعر: من الأعلى للأقل' : 'Price: High to Low'}</option>
              <option value="newest">{isArabic ? 'الأحدث' : 'Newest'}</option>
            </SortSelect>
          </MobileFilterSection>

          {/* Clear Filters Button */}
          {(selectedBrands.length > 0 || priceRange.min > 0 || priceRange.max < 1000 || sortBy !== 'popular' || showDiscountedOnly) && (
            <MobileClearFilters onClick={handleClearFilters}>
              {isArabic ? 'مسح الفلاتر' : 'Clear Filters'}
            </MobileClearFilters>
          )}
        </MobileFilterPanel>

        {/* Desktop Toolbar */}
        <DesktopToolbar>
          <Toolbar>
            <ToolbarTopRow>
              <ToolbarSection style={{ flex: '1 1 300px', minWidth: '250px' }}>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
              </ToolbarSection>
              
              {availableBrands.length > 0 && (
                <>
                  <FilterDivider />
                  <ToolbarSection style={{ flex: '1 1 300px', minWidth: '200px' }}>
                    <BrandsFilterContainer>
                      {availableBrands.map(brand => (
                        <BrandChip
                          key={brand.id}
                          $selected={selectedBrands.includes(brand.id)}
                          onClick={() => handleBrandToggle(brand.id)}
                        >
                          {brand.name}
                        </BrandChip>
                      ))}
                    </BrandsFilterContainer>
                  </ToolbarSection>
                </>
              )}
              
              <FilterDivider />
              
              <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="popular">{isArabic ? 'الأكثر مبيعاً' : 'Best Seller'}</option>
                <option value="price-low">{isArabic ? 'السعر: من الأقل للأعلى' : 'Price: Low to High'}</option>
                <option value="price-high">{isArabic ? 'السعر: من الأعلى للأقل' : 'Price: High to Low'}</option>
                <option value="newest">{isArabic ? 'الأحدث' : 'Newest'}</option>
              </SortSelect>
            </ToolbarTopRow>
            
            <ToolbarBottomRow>
              <PriceFilterContainer>
                <PriceLabel>{isArabic ? 'السعر' : 'Price'}</PriceLabel>
                <PriceSliderWrapper>
                  <PriceRangeSlider
                    min={0}
                    max={1000}
                    value={priceRange}
                    onChange={setPriceRange}
                    inline={true}
                  />
                </PriceSliderWrapper>
                <PriceDisplay>
                  {priceRange.min} - {priceRange.max} OMR
                </PriceDisplay>
              </PriceFilterContainer>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', borderLeft: '1px solid rgba(226, 232, 240, 0.6)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={showDiscountedOnly}
                    onChange={(e) => setShowDiscountedOnly(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#5efba9', cursor: 'pointer' }}
                  />
                  <span>{isArabic ? 'منتجات مخفضة' : 'Discounted Items'}</span>
                </label>
              </div>
            </ToolbarBottomRow>
          </Toolbar>
        </DesktopToolbar>
      </Header>

      <Content>

        <ProductsSection>
          {/* Show subcategories as tabs if viewing a specific category */}
          {category && subcategories.length > 0 && (
            <SubcategoriesSection>
              {subcategoriesLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  Loading subcategories...
                </div>
              ) : (
                <SubcategoriesTabs>
                  <SubcategoryTab
                    active={!selectedSubcategory}
                    onClick={() => {
                      setSelectedSubcategory(null);
                      if (categorySlug) {
                        navigate(`/category/${categorySlug}`);
                      }
                    }}
                  >
                    {isArabic ? 'الكل' : 'All'}
                  </SubcategoryTab>
                  {subcategories.map(subcat => {
                    const subcatSlug = subcat.slug || slugify(subcat.name_en || subcat.name_ar || '');
                    const subcatName = isArabic ? (subcat.name_ar || subcat.name_en || subcat.name || 'فئة فرعية') : (subcat.name_en || subcat.name || 'Subcategory');
                    return (
                      <SubcategoryTab
                        key={subcat.id}
                        active={selectedSubcategory === subcat.id}
                        onClick={() => {
                          setSelectedSubcategory(subcat.id);
                          if (categorySlug) {
                            navigate(`/category/${categorySlug}/${subcatSlug}`);
                          }
                        }}
                      >
                        {subcatName}
                      </SubcategoryTab>
                    );
                  })}
                </SubcategoriesTabs>
              )}
            </SubcategoriesSection>
          )}

          {/* Products Grid */}
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '24px' }}>
              {selectedSubcategory
                ? (isArabic
                  ? (subcategories.find(s => s.id === selectedSubcategory)?.name_ar || subcategories.find(s => s.id === selectedSubcategory)?.name_en || 'منتجات')
                  : (subcategories.find(s => s.id === selectedSubcategory)?.name_en || 'Products'))
                : category
                  ? (isArabic ? 'منتجات' : 'Products')
                  : (isArabic ? 'جميع المنتجات' : 'All Products')}
            </h2>
            {displayProducts.length > 0 ? (
              <>
                <ProductsGrid>
                  {displayProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </ProductsGrid>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <PaginationContainer>
                    <PaginationButton
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {isArabic ? <FiChevronRight /> : <FiChevronLeft />}
                      {isArabic ? 'السابق' : 'Previous'}
                    </PaginationButton>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                      // Show first page, last page, current page, and pages around current
                      const showPage = 
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                      
                      if (!showPage) {
                        // Show ellipsis
                        if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <PaginationInfo key={pageNum}>...</PaginationInfo>;
                        }
                        return null;
                      }
                      
                      return (
                        <PaginationButton
                          key={pageNum}
                          $active={currentPage === pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </PaginationButton>
                      );
                    })}
                    
                    <PaginationButton
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {isArabic ? 'التالي' : 'Next'}
                      {isArabic ? <FiChevronLeft /> : <FiChevronRight />}
                    </PaginationButton>
                    
                    <PaginationInfo>
                      {isArabic 
                        ? `صفحة ${currentPage} من ${totalPages} (${allDisplayProducts.length} ${allDisplayProducts.length === 1 ? 'منتج' : 'منتج'})`
                        : `Page ${currentPage} of ${totalPages} (${allDisplayProducts.length} ${allDisplayProducts.length === 1 ? 'product' : 'products'})`}
                    </PaginationInfo>
                  </PaginationContainer>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                {isArabic ? 'لم يتم العثور على منتجات مطابقة للفلاتر.' : 'No products found matching your filters.'}
              </div>
            )}
          </div>
        </ProductsSection>
      </Content>
    </Container>
  );
}

export default Categories;
